/**
 * Choosing what to grant. Never a box you type an id into.
 *
 * Puneet's requirement verbatim: "instead of mapping just segment IDs, admin won't know the segment ID,
 * so he should see a multi-select dropdown maybe with segment ID and segment name, along with audience
 * name if it's there." All three are shown together because the pair (label, what it resolves to) is
 * the only thing that catches a wrong id — 753 was granted for two weeks under a plausible label
 * because nothing ever displayed the two side by side.
 *
 * The consistency verdict rides along in the row rather than living on its own screen: a `mismatch` is
 * useful at the moment of choosing and nowhere else. A separate review screen is one an operator has to
 * think to visit.
 *
 * NEW rather than a library multi-select: this repo's Radix set is dialog/radio-group/slot/tabs/tooltip
 * — no Command, no Popover, no cmdk. Reuse would mean adding a dependency to render a filtered checkbox
 * list over an array already in memory.
 */
import { useMemo, useState } from 'react';

export interface PickerOption {
  /** Stable identity. Numeric for segments/audiences/surveys; the name itself for populations. */
  value: string;
  /** First line — what the operator recognises. */
  primary: string;
  /** Second line — the id, the audience, whatever confirms it is the right one. */
  secondary?: string;
  /** Renders as a warning chip; used for a `mismatch` verdict. */
  warning?: string;
  /** Already granted: shown, disabled, so it is obvious why it is not selectable. */
  alreadyGranted?: boolean;
}

interface ResourcePickerProps {
  options: PickerOption[];
  /** Verb on the submit button, e.g. "Grant segments". */
  submitLabel: string;
  onSubmit: (selected: string[]) => void;
  onCancel: () => void;
  busy?: boolean;
  emptyHint?: string;
}

export function ResourcePicker({
  options,
  submitLabel,
  onSubmit,
  onCancel,
  busy = false,
  emptyHint,
}: ResourcePickerProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    // Searches the secondary line too, so typing an id one DOES know still finds the row.
    return options.filter((o) =>
      `${o.primary} ${o.secondary ?? ''} ${o.value}`.toLowerCase().includes(needle),
    );
  }, [options, query]);

  const toggle = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  return (
    <div
      data-testid="picker"
      className="mt-2 rounded border border-gray-200 bg-white p-3 text-sm shadow-sm"
    >
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Filter by name or id…"
        aria-label="Filter"
        className="mb-2 w-full rounded border border-gray-300 px-2 py-1 text-sm"
      />

      <div className="max-h-64 overflow-y-auto">
        {visible.length === 0 ? (
          <p className="px-1 py-2 text-xs text-gray-500">
            {emptyHint || 'Nothing matches that filter.'}
          </p>
        ) : (
          visible.map((option) => (
            <label
              key={option.value}
              className={`flex items-start gap-2 rounded px-1 py-1.5 ${
                option.alreadyGranted ? 'opacity-50' : 'hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={selected.has(option.value)}
                disabled={option.alreadyGranted || busy}
                onChange={() => toggle(option.value)}
                // The accessible name has to carry the id, because that is what a test — and a
                // screen-reader user — identifies the row by.
                aria-label={`${option.primary}${option.secondary ? ` ${option.secondary}` : ''}`}
              />
              <span className="min-w-0">
                <span className="block truncate text-gray-900">{option.primary}</span>
                {option.secondary && (
                  <span className="block truncate text-xs text-gray-500">{option.secondary}</span>
                )}
                {option.warning && (
                  <span className="mt-0.5 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900">
                    {option.warning}
                  </span>
                )}
                {option.alreadyGranted && (
                  <span className="text-xs text-gray-500"> · already granted</span>
                )}
              </span>
            </label>
          ))
        )}
      </div>

      <div className="mt-2 flex items-center gap-2 border-t border-gray-100 pt-2">
        <button
          type="button"
          disabled={selected.size === 0 || busy}
          onClick={() => onSubmit([...selected])}
          className="rounded bg-teal-700 px-3 py-1 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-40"
        >
          {busy ? `${submitLabel}…` : submitLabel}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <span className="ml-auto text-xs text-gray-500">
          {selected.size > 0 ? `${selected.size} selected` : ''}
        </span>
      </div>
    </div>
  );
}

export default ResourcePicker;
